import React, { useEffect, useContext, useMemo } from 'react'
import { useHistory } from 'react-router-dom'

import styled from 'styled-components'

import { useDispatch, useSelector } from 'react-redux'

import pencil from '../../../../../assets/icons/pencil.svg'

import SkillsUpdate from './components/SkillsUpdate'
import EmploymentHistoryUpdate from '../../../../../components/applicantDetails/EmploymentHistoryUpdate'
import EducationalBackgroundUpdate from '../../../../../components/applicantDetails/EducationalBackgroundUpdate'
import CharacterReferenceUpdate from './components/CharacterReferenceUpdate'
import EmploymentHistory from '../../../../../components/applicantDetails/EmploymentHistory'
import EducationalBackground from '../../../../../components/applicantDetails/EducationalBackground'
import Skills from './components/Skills'
import CharacterReferences from '../../../../../components/applicantDetails/CharacterReferences'
import WFHSetup from './components/WFHSetup'
import RemoteFitness from './components/RemoteFitness'
import EmptyWrapper from '../../../../../components/EmptyWrapper'

import { ModalContext } from '../../../../../context/ModalContext'

import { setModalName } from '../../../../../redux/feature/modalSlice'
import {
  setExperiences,
  setProfessionalHeadlines,
  setEducationalBackground,
  setCharacterReferences,
  setTechnicalSkills,
  setSoftSkills,
} from '../../../../../redux/feature/applicantSlice'

import { getFromLocalStorage, parseJSON } from '../../../../../utils/util'

const MyCV = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const experiences = useSelector((state) => state.applicant.experiences)
  const educationalBackground = useSelector(
    (state) => state.applicant.educationalBackground
  )

  const professionalHeadlines = useSelector(
    (state) => state.applicant.professionalHeadlines
  )
  const technicalSkills = useSelector(
    (state) => state.applicant.technicalSkills
  )
  const softSkills = useSelector((state) => state.applicant.softSkills)

  const applicantCVData = getFromLocalStorage('applicant-cv-data')
  const characterReferences = JSON.parse(
    applicantCVData?.character_reference || '[]'
  )

  const modalName = useSelector((state) => state.modal.name)

  const { handleModalToggle, setRemotePage, setWorkEthicsPage } =
    useContext(ModalContext)

  const wfh = useMemo(() => getFromLocalStorage('applicant-wfh'), [])
  const cvData = useMemo(() => getFromLocalStorage('applicant-cv-data'), [])

  const meta = useMemo(
    () => (cvData.meta !== null ? JSON.parse(cvData.meta) : null),
    []
  )

  const computerSpec = useMemo(
    () => (wfh !== null ? JSON.parse(wfh.computer_specification) : wfh),
    []
  )
  const backupInfra = useMemo(() => parseJSON(wfh?.backup_infrastructure), [])

  const internetSpeedTest = useMemo(
    () => (wfh !== null ? JSON.parse(wfh.internet_speed_test) : wfh),
    []
  )
  const peripherals = useMemo(
    () => (wfh !== null ? JSON.parse(wfh.peripherals) : wfh),
    []
  )
  const remoteFitness = wfh !== null ? JSON.parse(wfh.meta) : wfh

  const handleEditExperiences = () => {
    handleModalToggle()
    dispatch(setModalName('employment'))
  }
  const handleEditEducationalBackground = () => {
    handleModalToggle()
    dispatch(setModalName('education'))
  }
  const handleEditSkills = () => {
    handleModalToggle()
    dispatch(setModalName('skill'))
  }
  const handleEditCharacterReferences = () => {
    handleModalToggle()
    dispatch(setModalName('characterRef'))
  }
  const handleEditWFHSetup = () => {
    setRemotePage(1)
    history.push('/remote-setup/update')
  }
  const handleEditRemoteFitness = () => {
    setWorkEthicsPage(1)
    history.push('/remote-fitness-level/update')
  }

  useEffect(() => {
    dispatch(setExperiences(meta?.experienceState || []))
    dispatch(setEducationalBackground(meta?.educationalData || []))

    const cachedCharacterReferences = parseJSON(cvData?.character_reference, [])
    dispatch(setCharacterReferences(cachedCharacterReferences))

    const cachedProfessionalHeadlines = parseJSON(
      cvData?.professional_headline,
      []
    )
    dispatch(setProfessionalHeadlines(cachedProfessionalHeadlines))

    const technicalSkills = meta?.fullSkills?.[0] || []
    const softSkills = meta?.fullSkills?.[1] || []

    dispatch(setTechnicalSkills(technicalSkills))
    dispatch(setSoftSkills(softSkills))
  }, [])

  // FIXME: WFH Setup and Remote Fitness Level not appearing after going to /profile immediately upon user account creation (uploaded cv)

  return (
    <>
      <SectionWrapper>
        <EmploymentHistory
          experiences={experiences}
          editIcon={pencil}
          handleEditModal={handleEditExperiences}
        />
      </SectionWrapper>
      <SectionWrapper>
        <EducationalBackground
          educationalData={educationalBackground}
          editIcon={pencil}
          handleEditModal={handleEditEducationalBackground}
        />
      </SectionWrapper>

      {meta !== null && (
        <SectionWrapper className="flex">
          <Skills
            professionalHeadlines={professionalHeadlines}
            technicalSkills={technicalSkills}
            softSkills={softSkills}
            editIcon={pencil}
            handleEditModal={handleEditSkills}
          />
        </SectionWrapper>
      )}

      {characterReferences && (
        <SectionWrapper>
          <CharacterReferences
            characterReferences={characterReferences}
            editIcon={pencil}
            handleEditModal={handleEditCharacterReferences}
          />
        </SectionWrapper>
      )}

      {wfh === null ? (
        <EmptyWrapper maxHeight="200px" marginBottom="1rem">
          You have no WFH setup.
        </EmptyWrapper>
      ) : (
        <SectionWrapper>
          <WFHSetup
            internetSpeedTest={internetSpeedTest}
            computerSpec={computerSpec}
            backupInfra={backupInfra}
            peripherals={peripherals}
            editIcon={pencil}
            handleEditModal={handleEditWFHSetup}
          />
        </SectionWrapper>
      )}

      <SectionWrapper>
        <RemoteFitness
          remoteFitness={remoteFitness}
          editIcon={pencil}
          handleEditModal={handleEditRemoteFitness}
        />
      </SectionWrapper>

      {modalName === 'employment' && <EmploymentHistoryUpdate saveToDatabase />}
      {modalName === 'education' && (
        <EducationalBackgroundUpdate saveToDatabase />
      )}
      {modalName === 'skill' && <SkillsUpdate />}
      {modalName === 'characterRef' && <CharacterReferenceUpdate />}
    </>
  )
}

const SectionWrapper = styled.section`
  margin-top: 0.5rem;
  min-height: 100px;
  padding: 10px 0;
  border-bottom: 1px solid #eaeaf8;
  position: relative;
  min-height: 100px;

  &.flex {
    display: flex;
    padding: 10px 0;
  }
`
export default MyCV
